import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useConfig } from "../../../context/ConfigContext";

const WarrantySupport = () => {
  const config = useConfig();
  const {
    WHATSAPP_NUMBER,
    CALL_PHONE_NUMBER,
    PHONE_NUMBER_FORM,
    EMAIL_ADDRESS,
  } = config;
  const warranties = [
    {
      component: "Paneles Solares",
      productWarranty: `15-25 años`,
      performanceWarranty: `25 años`,
      coverage: `90% rendimiento a 20 años`,
      icon: "Sun",
      details: [
        "Garantía del fabricante internacional",
        "Cobertura contra defectos de manufactura",
        "Garantía de rendimiento lineal",
        "Reemplazo gratuito en caso de falla",
      ],
    },
    {
      component: "Inversores",
      productWarranty: "5-10 años",
      performanceWarranty: "10 años",
      coverage: "Eficiencia garantizada >95%",
      icon: "Zap",
      details: [
        "Garantía extendida disponible",
        "Monitoreo remoto incluido",
        "Reemplazo express en 48 horas",
        "Soporte técnico especializado",
      ],
    },
    {
      component: "Instalación",
      productWarranty: "2 años",
      performanceWarranty: "5 años",
      coverage: "Mano de obra y materiales",
      icon: "Wrench",
      details: [
        "Certificación de instalación",
        "Garantía contra filtraciones",
        "Revisión anual gratuita",
        "Corrección de defectos sin costo",
      ],
    },
    {
      component: "Estructura de Montaje",
      productWarranty: "2 años",
      performanceWarranty: "5 años",
      coverage: "Resistencia a vientos y sismos",
      icon: "Shield",
      details: [
        "Materiales anticorrosivos",
        "Diseño antisísmico certificado",
        "Resistencia a vientos 150 km/h",
        "Garantía estructural completa",
      ],
    },
  ];

  const supportServices = [
    {
      title: "Soporte Técnico ",
      description: "Línea de emergencia disponible todo el tiempo",
      icon: "Phone",
      features: [
        "Atención telefónica",
        "Diagnóstico remoto",
        "Técnicos en sitio",
      ],
      contact: `WhatsApp: ${WHATSAPP_NUMBER}`,
    },
    {
      title: "Monitoreo Continuo",
      description: "Supervisión automática del rendimiento del sistema",
      icon: "Monitor",
      features: [
        "Dashboard web y App móvil",
        "Reportes mensuales",
        "Detección de problemas",
      ],
      contact: "Apps Fusión Solar (Huawei) o Shine (Growatt)",
    },
    {
      title: "Mantenimiento Preventivo",
      description: "Servicios programados para maximizar el rendimiento",
      icon: "Settings",
      features: [
        "Limpieza profesional trimestral",
        "Inspección técnica semestral",
        "Ajustes y calibraciones",
        "Reporte de estado completo",
      ],
      contact: "Programadas",
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Garantías y Soporte
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Protección completa para tu inversión con garantías extendidas y
            soporte técnico especializado
          </p>
        </div>

        {/* Warranty Coverage */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            Cobertura de Garantías
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {warranties?.map((warranty, index) => (
              <div
                key={index}
                className="bg-card rounded-2xl p-8 solar-shadow-card"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon
                      name={warranty?.icon}
                      size={24}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-text-primary">
                      {warranty?.component}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {warranty?.coverage}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-success/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-success mb-1">
                      {warranty?.productWarranty}
                    </div>
                    <div className="text-sm text-text-secondary">
                      Garantía de Producto
                    </div>
                  </div>
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-primary mb-1">
                      {warranty?.performanceWarranty}
                    </div>
                    <div className="text-sm text-text-secondary">
                      Garantía de Rendimiento
                    </div>
                  </div>
                </div>

                <ul className="space-y-2">
                  {warranty?.details?.map((detail, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Icon
                        name="Check"
                        size={16}
                        className="text-success flex-shrink-0"
                      />
                      <span className="text-sm text-text-secondary">
                        {detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Support Services */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            Servicios de Soporte
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportServices?.map((service, index) => (
              <div key={index} className="bg-muted/30 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <Icon
                      name={service?.icon}
                      size={24}
                      className="text-accent"
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-text-primary">
                      {service?.title}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {service?.description}
                    </p>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {service?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Icon
                        name="Check"
                        size={16}
                        className="text-success flex-shrink-0"
                      />
                      <span className="text-sm text-text-secondary">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-text-primary">
                      {service?.contact}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <div className="bg-white rounded-2xl p-8 solar-shadow-card max-w-2xl mx-auto">
            <Icon
              name="Headphones"
              size={48}
              className="text-primary mx-auto mb-4"
            />
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              ¿Necesitas Soporte Técnico?
            </h3>
            <p className="text-text-secondary mb-8">
              Nuestro equipo de expertos está disponible para ayudarte con
              cualquier consulta o problema técnico.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="bg-cta-orange hover:bg-cta-orange/90"
                href={`tel:${CALL_PHONE_NUMBER}`}
              >
                Llamar Soporte: {PHONE_NUMBER_FORM}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Mail"
                iconPosition="left"
                href={`mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(
                  "Solicitud de Soporte Técnico - Sistema Solar"
                )}&body=${encodeURIComponent(
                  `Estimado equipo de soporte técnico,
                
                Requiero asistencia técnica para mi sistema solar instalado.
        
                Por favor, programar visita técnica o brindar instrucciones para diagnóstico remoto.
                
                Datos de contacto:
                Teléfono: [Número]
                Mejor horario: [Especificar]
                
                Quedo atento a su pronta respuesta.
                
                Saludos cordiales.`
                )}`}
              >
                Enviar Email
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WarrantySupport;
